import BaseService from "./BaseService";
import { AuthStore } from "../stores/AuthStore";
import RootStore from "../stores/RootStore";

export type FileMediaSourceType = "audioFile" | "soundClip" | "montage";

interface PlaybackUrlResponse {
    playbackUrl: string;
    expiresAtUtc: string;
}

const PLAYBACK_URL_ENDPOINTS: Record<FileMediaSourceType, string> = {
    audioFile: "PlaybackUrl/AudioMedia",
    soundClip: "PlaybackUrl/SoundClip",
    montage: "PlaybackUrl/MontageAudio",
};

export class FilesService extends BaseService {
    protected urlBase = "api/Files";

    /**
     * Downloads a KBo artifact file (a deck or its thumbnail), reporting the
     * bytes received so far when `onProgress` is provided.
     */
    public async downloadAgentArtifact(
        storageAccountId: string,
        directoryId: string,
        fileName: string,
        onProgress?: (downloadedBytes: number) => void,
    ): Promise<Blob> {
        const params = new URLSearchParams({
            storageAccountId,
            directoryId,
            fileName,
        });
        // Undebounced: concurrent callers must not share one single-read body stream.
        const response = await this.getUndebounced<Response>(
            params,
            "DownloadAgentArtifact",
            undefined,
            true,
        );

        // Check for a progress callback before getReader(): taking a reader locks the body, so blob() would throw.
        if (!onProgress || !response.body) {
            return response.blob();
        }

        const contentType =
            response.headers.get("Content-Type") ?? "application/octet-stream";
        const reader = response.body.getReader();

        const chunks: BlobPart[] = [];
        let downloadedBytes = 0;
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            downloadedBytes += value.byteLength;
            onProgress(downloadedBytes);
            const chunk = new ArrayBuffer(value.byteLength);
            new Uint8Array(chunk).set(value);
            chunks.push(chunk);
        }

        return new Blob(chunks, { type: contentType });
    }

    private buildRequestParams(
        idParamName:
            | "amdId"
            | "soundClipId"
            | "montageId"
            | "dashboardId"
            | "fileName",
        idValue: string,
        refreshKey?: string,
    ): URLSearchParams {
        const params = new URLSearchParams({ [idParamName]: idValue });
        const headers = RootStore().getStore(AuthStore).getRequestHeader();
        const orgId = headers?.["Acx-OrganizationId"];

        if (orgId) {
            params.append("orgId", orgId);
        }
        if (refreshKey) {
            params.append("_refresh", refreshKey);
        }

        return params;
    }

    /**
     * Gets a short-lived signed playback URL for the given media file.
     * The returned URL can be set directly as an audio element's src —
     * no bearer token is needed since auth is embedded in the URL.
     */
    public async getPlaybackUrl(
        mediaId: string,
        mediaSourceType: FileMediaSourceType = "audioFile",
        refreshKey?: string,
    ): Promise<string> {
        const idParamName =
            mediaSourceType === "soundClip"
                ? "soundClipId"
                : mediaSourceType === "montage"
                ? "montageId"
                : "amdId";

        const params = this.buildRequestParams(
            idParamName,
            mediaId,
            refreshKey,
        );
        const endpoint = PLAYBACK_URL_ENDPOINTS[mediaSourceType];

        const response = await this.get<PlaybackUrlResponse>(
            params,
            endpoint,
            undefined,
            refreshKey ? `${endpoint}-${mediaId}-${refreshKey}` : undefined,
        );

        return response.playbackUrl;
    }

    /**
     * Gets a playback URL and the total file size for the given media file.
     * Uses a 1-byte Range request (bytes=0-0) to the streaming endpoint.
     * The server responds with 206 and a Content-Range header containing
     * the total size (e.g. "bytes 0-0/12345678"). Falls back to
     * Content-Length if the server returns 200 instead.
     */
    public async getAudioFileSize(
        mediaId: string,
        mediaSourceType: FileMediaSourceType = "audioFile",
        refreshKey?: string,
    ): Promise<{ playbackUrl: string; fileSize: number }> {
        const playbackUrl = await this.getPlaybackUrl(
            mediaId,
            mediaSourceType,
            refreshKey,
        );

        // The server returns an absolute URL (e.g. https://localhost:5001/...).
        // Convert to a relative path so the request goes through the Vite
        // proxy and stays same-origin.
        const url = new URL(playbackUrl, window.location.origin);
        const fetchUrl =
            url.origin === window.location.origin
                ? playbackUrl
                : url.pathname + url.search;

        const resp = await fetch(fetchUrl, {
            headers: { Range: "bytes=0-0" },
        });

        let fileSize = 0;

        if (resp.status === 206) {
            // Content-Range: bytes 0-0/12345678
            const contentRange = resp.headers.get("Content-Range");
            if (contentRange) {
                const match = contentRange.match(/\/(\d+)$/);
                if (match) {
                    fileSize = Number(match[1]);
                }
            }
        } else {
            // Fallback: server ignored Range and returned full response
            fileSize = Number(resp.headers.get("Content-Length") ?? 0);
        }

        // Discard the body so the connection can be reused.
        await resp.body?.cancel();

        return { playbackUrl, fileSize };
    }

    public async streamAudioMedia(amdId: string): Promise<Response> {
        const params = this.buildRequestParams("amdId", amdId);
        return this.get<Response>(
            params,
            "StreamAudioMedia",
            undefined,
            undefined,
            true,
        );
    }

    public async streamSoundClip(soundClipId: string): Promise<Response> {
        const params = this.buildRequestParams("soundClipId", soundClipId);
        return this.get<Response>(
            params,
            "StreamSoundClip",
            undefined,
            undefined,
            true,
        );
    }

    public async streamMontageAudio(montageId: string): Promise<Response> {
        const params = this.buildRequestParams("montageId", montageId);
        return this.get<Response>(
            params,
            "StreamMontageAudio",
            undefined,
            undefined,
            true,
        );
    }

    public async downloadAudioMedia(
        amdId: string,
        refreshKey?: string,
    ): Promise<Blob> {
        const params = this.buildRequestParams("amdId", amdId, refreshKey);

        return this.getBlob(
            params,
            "DownloadAudioMedia",
            refreshKey ? `DownloadAudioMedia-${amdId}-${refreshKey}` : undefined,
        );
    }

    public async downloadSoundClip(
        soundClipId: string,
        refreshKey?: string,
    ): Promise<Blob> {
        const params = this.buildRequestParams(
            "soundClipId",
            soundClipId,
            refreshKey,
        );

        return this.getBlob(
            params,
            "DownloadSoundClip",
            refreshKey
                ? `DownloadSoundClip-${soundClipId}-${refreshKey}`
                : undefined,
        );
    }

    public async downloadMontageAudio(
        montageId: string,
        refreshKey?: string,
    ): Promise<Blob> {
        const params = this.buildRequestParams(
            "montageId",
            montageId,
            refreshKey,
        );

        return this.getBlob(
            params,
            "DownloadMontageAudio",
            refreshKey
                ? `DownloadMontageAudio-${montageId}-${refreshKey}`
                : undefined,
        );
    }

    public async downloadDashboardLogo(dashboardId: string): Promise<Blob> {
        const params = this.buildRequestParams("dashboardId", dashboardId);

        return this.getBlob(params, "DownloadDashboardLogo");
    }

    public async downloadLogoFile(fileName: string): Promise<Blob> {
        const params = this.buildRequestParams("fileName", fileName);

        return this.getBlob(params, "DownloadLogoFile");
    }
}

export default FilesService;
